/**
 * Brief 3.6 Item 3 — TOTP 2FA enrollment + verification gate.
 *
 * Renders BEFORE the admin layout. On mount, hits /auth/totp/status:
 *  • enrolled=false → enrollment flow (QR + manual secret + first code +
 *    one-shot recovery codes display).
 *  • enrolled=true, session_verified=false → verification flow
 *    (6-digit TOTP or recovery code).
 *  • session_verified=true → render children.
 *
 * Charter v1.1 voice rules don't apply here — this is product chrome,
 * not Nerea output.
 */
import { useEffect, useState } from "react";
import { Loader2, ShieldCheck, KeyRound, Copy, AlertTriangle } from "lucide-react";
import api from "../../lib/api";

const ENROLL_INIT = "/auth/totp/enroll-init";
const ENROLL_CONFIRM = "/auth/totp/enroll-confirm";
const VERIFY = "/auth/totp/verify";
const STATUS = "/auth/totp/status";

export default function TotpGate({ children }) {
  const [phase, setPhase] = useState("checking"); // checking|enroll|verify|done|error
  const [error, setError] = useState("");

  // Enroll flow state
  const [provisioningUri, setProvisioningUri] = useState("");
  const [secret, setSecret] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState(null);

  // Code-entry state
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { (async () => {
    try {
      const { data } = await api.get(STATUS);
      if (data.session_verified) {
        setPhase("done");
      } else if (data.enrollment_required) {
        try {
          const { data: init } = await api.post(ENROLL_INIT);
          setProvisioningUri(init.provisioning_uri);
          setSecret(init.secret);
          setPhase("enroll");
        } catch {
          // enroll-init refused (e.g. already enrolled mid-race) — fall
          // back to the verify screen rather than blanking the UI.
          setPhase("verify");
        }
      } else {
        setPhase("verify");
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        // Not signed in at all — let the AdminLayout's <Navigate> handle it.
        setPhase("done");
        return;
      }
      // FAIL-OPEN: if /auth/totp/status is unreachable (network glitch,
      // backend redeploy in progress, deleted-endpoint cache, anything),
      // render the admin UI anyway. Backend `require_totp_verified`
      // still gates the sensitive endpoints, so this is defense-in-depth
      // not the primary lock. Better to let the admin see the UI and
      // surface the real error inline than show a blank screen.
      console.warn("[TotpGate] status check failed, falling open:", e);
      setPhase("done");
    }
  })(); }, []);

  const submitEnrollment = async () => {
    setSubmitting(true); setError("");
    try {
      const { data } = await api.post(ENROLL_CONFIRM, { code: code.trim() });
      setRecoveryCodes(data.recovery_codes);
      setCode("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Invalid code. Try the next one your app shows.");
    } finally { setSubmitting(false); }
  };

  const submitVerify = async () => {
    setSubmitting(true); setError("");
    try {
      await api.post(VERIFY, { code: code.trim() });
      setPhase("done");
    } catch (e) {
      setError(e?.response?.data?.detail || "Invalid code.");
    } finally { setSubmitting(false); }
  };

  if (phase === "checking") {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center font-body" data-testid="totp-checking">
        <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />
      </div>
    );
  }
  if (phase === "done") return children;
  if (phase === "error") {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center font-body">
        <div className="text-rose-300 text-sm" data-testid="totp-error">{error}</div>
      </div>
    );
  }

  // Enrollment screen — split: first show QR + code entry, then on success
  // show the recovery codes ONCE with an explicit "I've saved them" button.
  if (phase === "enroll") {
    if (recoveryCodes) {
      return <RecoveryCodesPanel codes={recoveryCodes} onAck={() => setPhase("done")} />;
    }
    return (
      <Shell title="Set up two-factor authentication" data-testid="totp-enroll-screen">
        <p className="text-zinc-400 text-[13px] leading-relaxed">
          Scan the QR code with Google Authenticator, 1Password, Authy, or any TOTP app.
          Then enter the 6-digit code the app shows.
        </p>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="rounded-xl border border-white/[0.08] bg-white p-4 flex items-center justify-center">
            <QRCode value={provisioningUri} />
          </div>
          <div className="space-y-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Or enter manually</div>
              <div className="mt-1.5 flex items-center gap-2">
                <code data-testid="totp-secret" className="rounded-md border border-white/[0.06] bg-white/[0.025] px-2.5 py-1.5 text-[12px] font-mono text-white tracking-wider">{secret}</code>
                <button type="button" onClick={() => navigator.clipboard.writeText(secret)} className="rounded-md border border-white/[0.08] bg-white/[0.025] hover:bg-white/[0.06] px-2 py-1.5 text-[11px] text-zinc-300">
                  <Copy className="h-3 w-3" />
                </button>
              </div>
            </div>
            <CodeInput value={code} onChange={setCode} onSubmit={submitEnrollment} placeholder="6-digit code" disabled={submitting} testid="totp-enroll-code" />
            <button type="button" onClick={submitEnrollment} disabled={submitting || code.trim().length < 6} data-testid="totp-enroll-submit" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-black hover:bg-zinc-200 disabled:bg-zinc-700 disabled:text-zinc-400 transition-colors px-4 py-2 text-[12.5px] font-medium">
              {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
              Confirm and finish setup
            </button>
            {error && <ErrorLine msg={error} />}
          </div>
        </div>
      </Shell>
    );
  }

  // verify
  return (
    <Shell title="Two-factor authentication" data-testid="totp-verify-screen">
      <p className="text-zinc-400 text-[13px] leading-relaxed">
        Enter the 6-digit code from your authenticator app — or one of your 8-character recovery codes (e.g. <code className="text-zinc-300 font-mono">A3BX-9PQK</code>).
      </p>
      <div className="mt-5 space-y-3 max-w-sm">
        <CodeInput value={code} onChange={setCode} onSubmit={submitVerify} placeholder="6-digit code  or  XXXX-XXXX" disabled={submitting} testid="totp-verify-code" />
        <button type="button" onClick={submitVerify} disabled={submitting || code.trim().length < 6} data-testid="totp-verify-submit" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-black hover:bg-zinc-200 disabled:bg-zinc-700 disabled:text-zinc-400 transition-colors px-4 py-2 text-[12.5px] font-medium">
          {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <KeyRound className="h-3.5 w-3.5" />}
          Verify
        </button>
        {error && <ErrorLine msg={error} />}
      </div>
    </Shell>
  );
}

function Shell({ title, children, ...rest }) {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center font-body p-6" {...rest}>
      <div className="w-full max-w-xl rounded-2xl border border-white/[0.06] bg-white/[0.015] p-7 shadow-2xl">
        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">NeptunePay back office</div>
        <h1 className="mt-1 text-[20px] font-semibold tracking-tight text-white">{title}</h1>
        <div className="mt-4">{children}</div>
      </div>
    </div>
  );
}

function CodeInput({ value, onChange, onSubmit, placeholder, disabled, testid }) {
  return (
    <input
      autoFocus
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => { if (e.key === "Enter") onSubmit(); }}
      placeholder={placeholder}
      disabled={disabled}
      className="w-full rounded-md border border-white/[0.10] bg-white/[0.03] px-3 py-2 text-[14px] text-white tracking-[0.18em] font-mono focus:outline-none focus:border-cyan-400/40"
    />
  );
}

function ErrorLine({ msg }) {
  return (
    <div data-testid="totp-error-line" className="flex items-start gap-1.5 text-[11.5px] text-rose-300">
      <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" /> <span>{msg}</span>
    </div>
  );
}

function RecoveryCodesPanel({ codes, onAck }) {
  const [acked, setAcked] = useState(false);
  return (
    <Shell title="Save your 10 recovery codes" data-testid="totp-recovery-screen">
      <p className="text-zinc-400 text-[13px] leading-relaxed">
        Each code works <span className="text-amber-300">once</span>. Store them somewhere only you can reach
        — a password manager is ideal. You won't see them again.
      </p>
      <div className="mt-4 grid grid-cols-2 gap-2 max-w-md" data-testid="totp-recovery-codes-list">
        {codes.map((c) => (
          <code key={c} className="rounded-md border border-white/[0.08] bg-white/[0.025] px-2.5 py-1.5 text-[12.5px] font-mono text-white tracking-wider text-center">
            {c}
          </code>
        ))}
      </div>
      <button type="button" onClick={() => navigator.clipboard.writeText(codes.join("\n"))} className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-white/[0.10] bg-white/[0.03] hover:bg-white/[0.06] px-3 py-1.5 text-[11.5px] text-zinc-300">
        <Copy className="h-3 w-3" /> Copy all
      </button>
      <label className="mt-5 flex items-center gap-2 text-[12px] text-zinc-300 cursor-pointer">
        <input type="checkbox" checked={acked} onChange={(e) => setAcked(e.target.checked)} data-testid="totp-recovery-ack-checkbox" className="rounded" />
        I've saved these codes somewhere safe.
      </label>
      <button type="button" disabled={!acked} onClick={onAck} data-testid="totp-recovery-continue" className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-white text-black hover:bg-zinc-200 disabled:bg-zinc-700 disabled:text-zinc-400 transition-colors px-5 py-2 text-[12.5px] font-medium">
        Continue to admin
      </button>
    </Shell>
  );
}

/**
 * Tiny inline QR renderer driven by Google's chart-API replacement — uses
 * `api.qrserver.com` (free, no auth). Falls back to a copy-able URI if
 * the image fails to load offline. Avoids pulling a full qrcode npm dep.
 */
function QRCode({ value }) {
  const src = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=0&data=${encodeURIComponent(value)}`;
  return (
    <img
      data-testid="totp-qr"
      src={src} alt="TOTP QR code" width={180} height={180}
      style={{ imageRendering: "pixelated" }}
    />
  );
}
